import * as z from "zod";

/**
 * The four pipeline stages a metric card can drill into. Each stage is anchored
 * on a different "entered this status at" timestamp, so the days-in-status
 * filter applies uniformly: `in_transit` → return.created_at,
 * `awaiting_processing` → return.delivered_at, `awaiting_storage` → most recent
 * `warehouse_return_processed` event for the source return, `in_storage` →
 * most recent `inventory_level.created_at` for a row in a bin/storage location.
 */
export const ProcessingPipelineStage = {
  inTransit: "in_transit",
  awaitingProcessing: "awaiting_processing",
  awaitingStorage: "awaiting_storage",
  inStorage: "in_storage",
} as const;
export type ProcessingPipelineStage =
  (typeof ProcessingPipelineStage)[keyof typeof ProcessingPipelineStage];

/**
 * How the row should be displayed in the unified pipeline table. Returns
 * (in_transit / awaiting_processing) are always `return`. Inventory rows
 * (awaiting_storage / in_storage) inherit the `inventory_item.kind` they were
 * stored under — `product` for a restocked merchant SKU, `return` for a
 * return-grade unit.
 */
export const ProcessingPipelineRowKind = {
  return_: "return",
  product: "product",
} as const;
export type ProcessingPipelineRowKind =
  (typeof ProcessingPipelineRowKind)[keyof typeof ProcessingPipelineRowKind];

export const processingPipelineRowSchema = z.object({
  /**
   * Stable row id. For return-stage rows this is the return id (Mongo hex).
   * For inventory-stage rows this is the inventory_item id (crockford32) so
   * the row stays addressable even when multiple items share a return.
   */
  id: z.string(),
  stage: z.enum([
    ProcessingPipelineStage.inTransit,
    ProcessingPipelineStage.awaitingProcessing,
    ProcessingPipelineStage.awaitingStorage,
    ProcessingPipelineStage.inStorage,
  ]),
  kind: z.enum([
    ProcessingPipelineRowKind.return_,
    ProcessingPipelineRowKind.product,
  ]),
  /** Hex return id for both return-stage and inventory-stage rows. Null if absent. */
  returnId: z.string().nullable(),
  teamId: z.string().nullable(),
  teamName: z.string().nullable(),
  /** Order name on the source return — null when not resolvable. */
  orderName: z.string().nullable(),
  customerName: z.string().nullable(),
  /** Product title for `kind === "product"` rows; null otherwise. */
  productTitle: z.string().nullable(),
  /** Product SKU for `kind === "product"` rows; null otherwise. */
  productSku: z.string().nullable(),
  /** Bin name for `in_storage` rows; null otherwise. */
  currentLocation: z.string().nullable(),
  /**
   * The "entered this status" anchor as ISO. Drives the days-in-status column
   * and the min/max filter. Null when the anchor can't be derived.
   */
  enteredStatusAt: z.string().nullable(),
  /** Calendar days from `enteredStatusAt` to snapshot time. Null when anchor null. */
  daysInStatus: z.number().nullable(),
});

export type ProcessingPipelineRow = z.infer<typeof processingPipelineRowSchema>;

export const getProcessingPipelineReturns = {
  name: "getProcessingPipelineReturns" as const,
  input: z.object({
    stage: z.enum([
      ProcessingPipelineStage.inTransit,
      ProcessingPipelineStage.awaitingProcessing,
      ProcessingPipelineStage.awaitingStorage,
      ProcessingPipelineStage.inStorage,
    ]),
    returnsCenterId: z.string().optional(),
    teamIds: z.array(z.string()).optional(),
    /** Minimum days the row has spent in the selected stage. */
    minDaysInStatus: z.number().int().nonnegative().optional(),
    /** Maximum days the row has spent in the selected stage. */
    maxDaysInStatus: z.number().int().nonnegative().optional(),
  }),
  output: z.object({
    rows: z.array(processingPipelineRowSchema),
    /** True when the result was capped — the table footnotes the cap. */
    truncated: z.boolean(),
    /** Server time the snapshot was computed (ISO). */
    generatedAt: z.string(),
  }),
};

export type GetProcessingPipelineReturnsOutput = z.infer<
  (typeof getProcessingPipelineReturns)["output"]
>;
